import { useCallback, useEffect, useRef, useState } from 'react';
import resolveApiBase from '../../tradingCockpit/apiBase';

const API_BASE = resolveApiBase();
const API_KEY = process.env.REACT_APP_API_KEY;
const POLL_MS = 3000;
const EQUITY_POLL_MS = 60000;

const resolveUrl = (path) => {
    if (!API_BASE) return path;
    const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

const headers = (extra = {}) => ({ Accept: 'application/json', ...(API_KEY ? { 'x-api-key': API_KEY } : {}), ...extra });

async function getJson(path) {
    const resp = await fetch(resolveUrl(path), { headers: headers(), cache: 'no-store' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return resp.json();
}

/**
 * Kubera desk data: /api/strategies/kubera/desk every 3s, /equity every minute, and the
 * kill / pause control call.
 */
export function useKuberaDesk(enabled = true) {
    const [data, setData] = useState(null);
    const [equity, setEquity] = useState(null);
    const [error, setError] = useState(null);
    const [fetchedAt, setFetchedAt] = useState(null);
    const alive = useRef(true);

    const fetchDesk = useCallback(async () => {
        try {
            const payload = await getJson('/api/strategies/kubera/desk');
            if (!alive.current) return;
            setData(payload);
            setError(null);
            setFetchedAt(new Date().toISOString());
        } catch (err) {
            if (alive.current) setError(err.message || String(err));
        }
    }, []);

    const fetchEquity = useCallback(async () => {
        try {
            const payload = await getJson('/api/strategies/kubera/equity?days=120');
            if (alive.current) setEquity(payload);
        } catch (_err) { /* equity is secondary; the desk keeps working */ }
    }, []);

    const control = useCallback(async (action, book, note) => {
        const resp = await fetch(resolveUrl('/api/strategies/kubera/control'), {
            method: 'POST',
            headers: headers({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ action, book, note }),
        });
        const body = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(body.message || `HTTP ${resp.status}`);
        fetchDesk();
        return body;
    }, [fetchDesk]);

    useEffect(() => {
        alive.current = true;
        if (!enabled) return () => { alive.current = false; };
        fetchDesk();
        fetchEquity();
        const a = window.setInterval(fetchDesk, POLL_MS);
        const b = window.setInterval(fetchEquity, EQUITY_POLL_MS);
        return () => {
            alive.current = false;
            window.clearInterval(a);
            window.clearInterval(b);
        };
    }, [enabled, fetchDesk, fetchEquity]);

    return { data, equity, error, fetchedAt, control, refresh: fetchDesk };
}

export default useKuberaDesk;
